import { apiClient, buildWebSocketUrl } from './client'
import type { CreateTaskResult, PalletWorkItem, TaskBoxesResponse, TaskCreationMode, TaskKMCodesResponse, TaskPalletsResponse, TaskSummary, UpdateTaskDatesDto, UpdateTaskDatesResult } from '@/types'
import { DEFAULT_PARSE_CONFIG } from '@/types'

export interface ProductGroupOption {
  code: string
  name: string
}

export const tasksApi = {
  getAll: () => apiClient.get<TaskSummary[]>('/api/tasks'),

  getByUuid: (uuid: string) => apiClient.get<TaskSummary>(`/api/tasks/${uuid}`),

  create: (formData: FormData) =>
    apiClient.post<CreateTaskResult>('/api/tasks/create', formData, {
      timeout: 300_000,
    }),

  delete: (uuid: string) => apiClient.delete(`/api/tasks/${uuid}`),

  createShipment: (uuid: string) =>
    apiClient.post<{ shipment: { id: string }; additions_count: number; items_count: number }>(
      `/api/tasks/${uuid}/create-shipment`,
      {},
      { timeout: 300_000 },
    ),

  deleteAll: () => apiClient.delete<{ deleted: number }>('/api/tasks'),

  listProductGroups: () =>
    apiClient.get<ProductGroupOption[]>('/api/product-groups'),

  getKMCodes: (uuid: string, params?: { limit?: number; offset?: number; q?: string }) =>
    apiClient.get<TaskKMCodesResponse>(`/api/tasks/${uuid}/codes`, { params }),

  getBoxes: (uuid: string, params?: { limit?: number; offset?: number; q?: string; pallet_num?: number }) =>
    apiClient.get<TaskBoxesResponse>(`/api/tasks/${uuid}/boxes`, { params }),

  getPallets: (uuid: string, params?: { limit?: number; offset?: number; q?: string }) =>
    apiClient.get<TaskPalletsResponse>(`/api/tasks/${uuid}/pallets`, { params }),

  assignDates: (uuid: string, data: UpdateTaskDatesDto) =>
    apiClient.post<UpdateTaskDatesResult>(`/api/tasks/${uuid}/dates`, data),

  takePallet: (
    uuid: string,
    palletNum: number,
    workerName: string,
    dates?: { production_date?: string; expiry_date?: string },
  ) =>
    apiClient.post<{ item: PalletWorkItem }>(`/api/tasks/${uuid}/pallets/${palletNum}/take`, {
      worker_name: workerName,
      ...dates,
    }),

  releasePallet: (uuid: string, palletNum: number, workerName: string) =>
    apiClient.post<{ item: PalletWorkItem }>(`/api/tasks/${uuid}/pallets/${palletNum}/release`, {
      worker_name: workerName,
    }),

  countersWebSocketUrl: (uuid: string) =>
    buildWebSocketUrl(`/api/tasks/${encodeURIComponent(uuid)}/ws`),

  workerPalletsWebSocketUrl: (uuid: string, workerName: string) =>
    buildWebSocketUrl(
      `/api/tasks/${encodeURIComponent(uuid)}/worker-ws?worker_name=${encodeURIComponent(workerName)}`,
    ),
}

/** Build multipart body for task create. */
export function buildTaskCreateFormData(input: {
  creationMode: TaskCreationMode
  productGroupCode: string
  itemsPerBox: number
  boxesPerPallet: number
  name?: string
  fileKm?: File | null
  fileKaBoxes: File
  fileKaPallets: File
}): FormData {
  const fd = new FormData()
  fd.append('creation_mode', input.creationMode)
  fd.append('product_group_code', input.productGroupCode)
  fd.append('items_per_box', String(input.itemsPerBox))
  fd.append('boxes_per_pallet', String(input.boxesPerPallet))
  if (input.name?.trim()) fd.append('name', input.name.trim())
  if (input.fileKm) fd.append('file_km', input.fileKm)
  fd.append('file_ka_boxes', input.fileKaBoxes)
  fd.append('file_ka_pallets', input.fileKaPallets)
  fd.append('km_parse_config', JSON.stringify(DEFAULT_PARSE_CONFIG))
  fd.append('ka_parse_config', JSON.stringify(DEFAULT_PARSE_CONFIG))
  return fd
}
